import type { SceneStore } from '@os/doc';
import type { Camera } from '@os/engine';

/** Where the active page is written (`?page=` of the board URL, P14 Ruling 13); `write(null)` removes it. */
export type PageUrl = { read(): string | null; write(id: string | null): void };

/** `?page=` through `history.replaceState`: switching pages never adds a browser history entry. */
export const locationPageUrl: PageUrl = {
  read: () => new URLSearchParams(window.location.search).get('page'),
  write: (id) => {
    const url = new URL(window.location.href);
    if (id === null) url.searchParams.delete('page');
    else url.searchParams.set('page', id);
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url);
  },
};

/** A `?page=` kept in memory (a version preview inside the board: the board's own URL stays untouched). */
export function memoryPageUrl(): PageUrl {
  let value: string | null = null;
  return { read: () => value, write: (id) => void (value = id) };
}

const isCamera = (v: unknown): v is Camera =>
  !!v && typeof v === 'object' && ['x', 'y', 'z'].every((k) => Number.isFinite((v as Record<string, unknown>)[k])) && (v as Camera).z > 0;

/**
 * Page navigation of one open board (P14 Ruling 13): follows the store's active page whoever changes it (the pages
 * panel, a page link, a comment, an undo, a peer deleting the page) into the URL, keeps the stack that « ← Page
 * précédente » pops, and remembers each page's camera for the tab's session.
 */
export class PageNav {
  private readonly stack: string[] = [];
  private readonly cameras = new Map<string, Camera>();
  private readonly subs = new Set<() => void>();
  private unsubscribe: Array<() => void> = [];

  /** Opens the page `?page=` names (an unknown one: the first page, and the URL says so). Following starts with `start`. */
  constructor(private readonly o: { store: SceneStore; boardId: string; storage: Storage | null; url: PageUrl }) {
    const wanted = o.url.read();
    if (wanted !== null) o.store.setPage(wanted);
    this.writeUrl();
  }

  /** Follows the store's page into the URL until the returned stop (or `destroy`). */
  start(): () => void {
    this.stop();
    const follow = () => {
      this.writeUrl();
      this.notify();
    };
    this.unsubscribe = [this.o.store.subscribePage(follow), this.o.store.subscribePages(follow)];
    return () => this.stop();
  }

  /** Shows `id`; `push` (a page link) lets « back » return to the current page. */
  go(id: string, opts: { push?: boolean } = {}): void {
    if (id === this.o.store.page) return;
    if (opts.push) this.stack.push(this.o.store.page);
    this.o.store.setPage(id);
    this.notify();
  }

  /** Back to the last page a link left that still exists; false when there is none. */
  back(): boolean {
    const known = new Set(this.o.store.pages().map((p) => p.id));
    while (this.stack.length) {
      const id = this.stack.pop()!;
      if (known.has(id) && id !== this.o.store.page) {
        this.o.store.setPage(id);
        this.notify();
        return true;
      }
    }
    this.notify();
    return false;
  }

  get canGoBack(): boolean {
    const known = new Set(this.o.store.pages().map((p) => p.id));
    return this.stack.some((id) => known.has(id) && id !== this.o.store.page);
  }

  /** The camera last used on `id` in this tab's session, null on a first visit. */
  cameraFor(id: string): Camera | null {
    const kept = this.cameras.get(id);
    if (kept) return kept;
    try {
      const raw = this.o.storage?.getItem(this.key(id));
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return isCamera(parsed) ? { x: parsed.x, y: parsed.y, z: parsed.z } : null;
    } catch {
      return null;
    }
  }

  saveCamera(id: string, c: Camera): void {
    this.cameras.set(id, c);
    try {
      this.o.storage?.setItem(this.key(id), JSON.stringify({ x: c.x, y: c.y, z: c.z }));
    } catch {
      // private mode, full storage: the camera is kept in memory only
    }
  }

  subscribe(cb: () => void): () => void {
    this.subs.add(cb);
    return () => void this.subs.delete(cb);
  }

  destroy(): void {
    this.stop();
    this.subs.clear();
  }

  private stop(): void {
    this.unsubscribe.forEach((u) => u());
    this.unsubscribe = [];
  }

  private key(id: string): string {
    return `os.camera.${this.o.boardId}.${id}`;
  }

  /** `?page=` names the active page once the board has several; a one-page board keeps a clean URL. */
  private writeUrl(): void {
    const one = this.o.store.pages().length < 2;
    const want = one ? null : this.o.store.page;
    if (this.o.url.read() !== want) this.o.url.write(want);
  }

  private notify(): void {
    this.subs.forEach((cb) => cb());
  }
}
